/**
 * Redux Data Adapter for InstructorGradebookV2
 *
 * Transforms the V1 gradebookGridData structure (2D row array) to the V2 format
 * expected by the Gradebook component.
 *
 * V1 gradebookGridData structure:
 * - Row 0: Categories (header row with category names)
 * - Row 1: Assignments (header row with assignment names)
 * - Row 2: Averages (class averages for each assignment)
 * - Row 3+: Student rows with grades
 *
 * V2 expected format:
 * - students: Array of student objects
 * - assignments: Array of assignment objects
 * - categories: Array of category objects
 * - grades: Array of student-assignment grade objects
 */

/**
 * Extract categories from the gradebook grid data
 * @param {Array} categoriesRow - Row 0 of gradebookGridData
 * @param {Array} assignmentsRow - Row 1 of gradebookGridData (to get isUngraded from aggregate cells)
 * @param {number} leftGridColumnCount - Number of frozen columns
 * @returns {Array} Array of category objects
 */
function extractCategories(categoriesRow, assignmentsRow, leftGridColumnCount) {
  if (!categoriesRow || categoriesRow.length === 0) {
    return [];
  }

  const categories = [];
  const seenCategoryIds = new Set();

  // Skip the left frozen columns (name, optionally student ID, course total)
  for (let i = leftGridColumnCount; i < categoriesRow.length; i++) {
    const cell = categoriesRow[i];
    if (
      cell &&
      cell.gradebook_category_id &&
      !seenCategoryIds.has(cell.gradebook_category_id)
    ) {
      seenCategoryIds.add(cell.gradebook_category_id);

      // Find the aggregate cell for this category to get isUngraded status
      const aggregateCell = assignmentsRow?.find(
        (c) =>
          c &&
          c.isAggregateCell &&
          c.gradebook_category_id === cell.gradebook_category_id,
      );

      categories.push({
        id: String(cell.gradebook_category_id),
        name: cell.gradebook_category_name || cell.headerContent || 'Unknown',
        weight: cell.weight || 0,
        dropLowestNumGrades: cell.drop_lowest_num_grades || 0,
        color: getCategoryColor(categories.length),
        isCollapsed: false,
        isUngraded: aggregateCell?.isUngraded || false,
      });
    }
  }

  return categories;
}

/**
 * Extract assignments from the gradebook grid data
 * @param {Array} assignmentsRow - Row 1 of gradebookGridData
 * @param {number} leftGridColumnCount - Number of frozen columns
 * @returns {Array} Array of assignment objects
 */
function extractAssignments(assignmentsRow, leftGridColumnCount) {
  if (!assignmentsRow || assignmentsRow.length === 0) {
    return [];
  }

  const assignments = [];

  // Skip the left frozen columns
  for (let i = leftGridColumnCount; i < assignmentsRow.length; i++) {
    const cell = assignmentsRow[i];

    // Skip aggregate/category total cells
    if (cell && cell.id && !cell.isAggregateCell) {
      // Get effective due date
      let effectiveDueDate = cell.assignment_due_at;
      if (!effectiveDueDate && cell.Exceptions) {
        // Fall back to assignment exception due date
        const assignmentException = cell.Exceptions.find(
          (ex) =>
            ex.assignment_id === cell.id && ex.exception_type === 'assignment',
        );
        effectiveDueDate = assignmentException?.exception_value?.due_date;
      }

      const formattedEffectiveDueDate = effectiveDueDate
        ? new Date(effectiveDueDate)
        : null;

      assignments.push({
        id: String(cell.id),
        name: cell.name || cell.headerContent || 'Unknown',
        category: String(cell.gradebook_category_id),
        dueDate: formattedEffectiveDueDate,
        totalPoints: cell.assigned_points || 100,
        weight: cell.weight || 0,
        gradingType: cell.grading_type,
        isUngraded: cell.isUngraded || false,
        Exceptions: cell.Exceptions || [],
        // For ToolLabel component
        tool: cell.tool || null,
        subtype: cell.subtype || null,
        parent_id: cell.parent_id || null,
        isFinalAssignment: cell.isFinalAssignment || false,
        finalDraftNumber: cell.finalDraftNumber || null,
        // For isAssignmentInProgress check
        assignment_due_at: formattedEffectiveDueDate,
        // Original field names needed by ManualAssignments modal
        assigned_points: cell.assigned_points,
        gradebook_category_id: cell.gradebook_category_id,
        grading_type: cell.grading_type,
      });
    }
  }

  return assignments;
}

/**
 * Extract students and their grades from the gradebook grid data
 * @param {Array} rows - All rows from gradebookGridData
 * @param {number} leftGridColumnCount - Number of frozen columns
 * @param {Array} assignmentsRow - Row 1 containing assignment metadata
 * @returns {Object} { students, grades, categoryTotals }
 */
function extractStudentsAndGrades(rows, leftGridColumnCount, assignmentsRow) {
  if (!rows || rows.length < 4) {
    return { students: [], grades: [], categoryTotals: {} };
  }

  const students = [];
  const grades = [];
  const categoryTotals = {}; // Map of studentId -> categoryId -> category total data

  // Student rows start at index 3 (after categories, assignments, averages)
  for (let rowIndex = 3; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex];
    if (!row || row.length === 0) continue;

    // First cell is always the student name cell
    const studentCell = row[0];
    if (!studentCell || !studentCell.studentId) continue;

    // Find the course total cell (has isCourseTotalColumn: true) and extract the pre-calculated grade
    const courseTotalCell = row.find(
      (cell) => cell && cell.isCourseTotalColumn,
    );
    const courseTotal = courseTotalCell?.grade;
    // Extract points data for course total (needed for points mode display)
    const coursePointsEarned = courseTotalCell?.pointsEarned;
    const coursePointsPossible = courseTotalCell?.pointsPossible;

    // Find Student ID cell if present (has isStudentIdColumn: true)
    const studentIdCell = row.find((cell) => cell && cell.isStudentIdColumn);
    const thirdPartyUserId = studentIdCell?.headerContent || '';

    const student = {
      id: String(studentCell.studentId),
      name: `${studentCell.firstName || ''} ${studentCell.lastName || ''}`.trim(),
      firstName: studentCell.firstName || '',
      lastName: studentCell.lastName || '',
      email: studentCell.email || '',
      studentNumber: studentCell.studentNumber || '',
      isBrokenSyncReefGrades: studentCell.isBrokenSyncReefGrades || false,
      // Store pre-calculated course total from V1 data
      courseTotal: courseTotal,
      // Store course points for points mode display
      coursePointsEarned: coursePointsEarned,
      coursePointsPossible: coursePointsPossible,
      // Store third party user ID for Student ID column
      thirdPartyUserId: thirdPartyUserId,
    };

    students.push(student);

    // Initialize category totals for this student
    categoryTotals[student.id] = {};

    // Extract grades for this student
    // Skip frozen columns and iterate through assignment columns
    for (
      let colIndex = leftGridColumnCount;
      colIndex < row.length;
      colIndex++
    ) {
      const gradeCell = row[colIndex];
      const assignmentCell = assignmentsRow[colIndex];

      if (!gradeCell || !assignmentCell) continue;

      // Handle category total (aggregate) cells - extract pre-calculated data
      if (gradeCell.isAggregateCell && assignmentCell.isAggregateCell) {
        const categoryId = String(assignmentCell.gradebook_category_id);
        categoryTotals[student.id][categoryId] = {
          // Pre-calculated grade as decimal (0-1)
          studentGrade: gradeCell.studentGrade,
          // Points data for points mode display (V1 uses pointsEarned/pointsPossible OR studentPoints/assigned_points)
          studentPoints: gradeCell.studentPoints ?? gradeCell.pointsEarned,
          assigned_points:
            gradeCell.assigned_points ?? gradeCell.pointsPossible,
          isUngraded: gradeCell.isUngraded || false,
          numCompleted: gradeCell.numCompleted || 0,
          weightedCategoryTotal: gradeCell.weightedCategoryTotal || 0,
        };
        continue;
      }

      // Skip non-assignment cells
      if (gradeCell.isAggregateCell || assignmentCell.isAggregateCell) {
        continue;
      }

      // Header columns follow the first student on the page; each body row is built
      // from that row's assignments—so a column index is not always the same assignment.
      // Prefer gradeCell.id (from getStudentGradeObj) when present.
      let assignmentId = '';
      if (gradeCell.id != null && gradeCell.id !== '') {
        assignmentId = String(gradeCell.id);
      } else if (assignmentCell.id != null) {
        assignmentId = String(assignmentCell.id);
      }

      if (!assignmentId) continue;

      grades.push({
        id: assignmentId,
        assignmentId: assignmentId,
        studentId: student.id,
        // Keep raw decimal grade (0-1) for proper formatting with shared utilities
        studentGrade: gradeCell.studentGrade,
        studentPoints: gradeCell.studentPoints,
        assigned_points: gradeCell.assigned_points || 0,
        // Keep legacy fields for compatibility
        pointsEarned: gradeCell.studentPoints || 0,
        pointsPossible: gradeCell.assigned_points || 0,
        isOverridden: gradeCell.isOverridden || false,
        isLate: gradeCell.receivedLatePenalty || false,
        isDropped: gradeCell.isDropped || false,
        exceptions: gradeCell.Exceptions || [],
        // Additional fields needed for proper grade formatting
        status: gradeCell.status,
        isAssigned: gradeCell.isAssigned,
        tool: gradeCell.tool,
        subtype: gradeCell.subtype,
        grading_type: gradeCell.grading_type,
        // Additional fields needed for grade override dialog (matching V1)
        gradeComment: gradeCell.gradeComment || '',
        dateCompleted: gradeCell.dateCompleted || null,
        isInstructorEntered: gradeCell.isInstructorEntered || false,
        isOverdue: gradeCell.isOverdue || false,
        studentAssignmentDueAt: gradeCell.studentAssignmentDueAt || null,
        hasLaunched: gradeCell.hasLaunched || false,
        course_id: gradeCell.course_id || null,
        isUngraded: gradeCell.isUngraded || false,
        receivedLatePenalty: gradeCell.receivedLatePenalty || false,
      });
    }
  }

  return { students, grades, categoryTotals };
}

/**
 * Extract averages from the gradebook grid data
 * @param {Array} averagesRow - Row 2 of gradebookGridData
 * @param {number} leftGridColumnCount - Number of frozen columns
 * @param {Array} assignmentsRow - Row 1 containing assignment metadata
 * @param {Array} categories - Extracted categories
 * @returns {Object} Averages object with courseAverage, categoryAverages, assignmentAverages
 */
function extractAverages(
  averagesRow,
  leftGridColumnCount,
  assignmentsRow,
  categories,
) {
  const averages = {
    courseAverage: 0,
    categoryAverages: {},
    assignmentAverages: {},
  };

  if (!averagesRow || averagesRow.length === 0) {
    return averages;
  }

  // Extract assignment and category averages
  for (let i = leftGridColumnCount; i < averagesRow.length; i++) {
    const avgCell = averagesRow[i];
    const assignmentCell = assignmentsRow ? assignmentsRow[i] : null;

    if (!avgCell) continue;

    // Check if this is a category total column
    if (
      avgCell.isAggregateCell &&
      assignmentCell &&
      assignmentCell.gradebook_category_id
    ) {
      const categoryId = String(assignmentCell.gradebook_category_id);
      const avg =
        avgCell.pointsPossible > 0
          ? (avgCell.pointsEarned / avgCell.pointsPossible) * 100
          : 0;
      averages.categoryAverages[categoryId] = avg;
    } else if (
      assignmentCell &&
      assignmentCell.id &&
      !assignmentCell.isAggregateCell
    ) {
      // Assignment average
      const assignmentId = String(assignmentCell.id);
      const avg =
        avgCell.assigned_points > 0
          ? (avgCell.pointsEarned / avgCell.assigned_points) * 100
          : 0;
      averages.assignmentAverages[assignmentId] = avg;
    }
  }

  // Calculate course average from category averages
  if (categories.length > 0) {
    let totalWeight = 0;
    let weightedSum = 0;
    categories.forEach((cat) => {
      const catAvg = averages.categoryAverages[cat.id] || 0;
      weightedSum += catAvg * (cat.weight || 0);
      totalWeight += cat.weight || 0;
    });
    averages.courseAverage = totalWeight > 0 ? weightedSum / totalWeight : 0;
  }

  return averages;
}

/**
 * Get a color for a category based on its index
 */
function getCategoryColor(index) {
  const colors = [
    '#3B82F6', // blue
    '#10B981', // green
    '#F59E0B', // amber
    '#EF4444', // red
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#06B6D4', // cyan
    '#F97316', // orange
  ];
  return colors[index % colors.length];
}

/**
 * Transform Redux gradebookGridData to V2 format
 * @param {Object} gradebookGridData - The gradebook grid data from Redux
 * @param {Object} options - Additional options
 * @param {boolean} options.showPercent - Whether to show percentages
 * @param {Array} options.collapsedCategoryIds - IDs of collapsed categories
 * @returns {Object} V2 format data
 */
export function transformReduxToV2Format(gradebookGridData, options = {}) {
  if (
    !gradebookGridData ||
    !gradebookGridData.rows ||
    gradebookGridData.rows.length === 0
  ) {
    return {
      students: [],
      assignments: [],
      categories: [],
      grades: [],
      averages: {
        courseAverage: 0,
        categoryAverages: {},
        assignmentAverages: {},
      },
      totalStudents: 0,
      pageSize: 50,
      pageCount: 1,
      hasStudents: false,
      hasAssignments: false,
    };
  }

  const {
    rows,
    leftGridColumnCount = 2,
    hasStudents,
    hasAssignments,
    rowCount,
  } = gradebookGridData;

  // Row 0: Categories
  // Row 1: Assignments
  // Row 2: Averages
  // Row 3+: Students
  const categoriesRow = rows[0];
  const assignmentsRow = rows[1];
  const averagesRow = rows[2];

  const categories = extractCategories(
    categoriesRow,
    assignmentsRow,
    leftGridColumnCount,
  );
  const assignments = extractAssignments(assignmentsRow, leftGridColumnCount);
  const { students, grades, categoryTotals } = extractStudentsAndGrades(
    rows,
    leftGridColumnCount,
    assignmentsRow,
  );
  const averages = extractAverages(
    averagesRow,
    leftGridColumnCount,
    assignmentsRow,
    categories,
  );

  return {
    students,
    assignments,
    categories,
    grades,
    averages,
    categoryTotals, // Pre-calculated category totals from V1
    totalStudents: rowCount || students.length,
    pageSize: students.length || 50,
    pageCount: options.pageCount || 1,
    hasStudents: hasStudents || students.length > 0,
    hasAssignments: hasAssignments || assignments.length > 0,
    showPercent: options.showPercent || false,
    collapsedCategoryIds: options.collapsedCategoryIds || [],
  };
}

/**
 * Create pre-calculated gradebook data from V2 format
 * This replicates the calculations.js structure for V2
 * @param {Object} v2Data - Data in V2 format
 * @returns {Object} Calculations object with studentCalculations and averages
 */
export function createCalculationsFromV2Data(v2Data) {
  if (!v2Data || v2Data.students.length === 0) {
    return {
      studentCalculations: {},
      averages: v2Data?.averages || {
        courseAverage: 0,
        categoryAverages: {},
        assignmentAverages: {},
      },
    };
  }

  // Build grade index for O(1) lookups
  const gradeIndex = new Map();
  for (const grade of v2Data.grades) {
    if (!gradeIndex.has(grade.studentId)) {
      gradeIndex.set(grade.studentId, new Map());
    }
    gradeIndex.get(grade.studentId).set(grade.assignmentId, grade);
  }

  const studentCalculations = {};

  for (const student of v2Data.students) {
    const studentGrades = gradeIndex.get(student.id) || new Map();

    // Use pre-extracted category totals from V1 data
    // This includes studentGrade, numCompleted, isUngraded, etc.
    const studentCategoryTotals = v2Data.categoryTotals?.[student.id] || {};

    // Build categoryTotals object for display
    // For graded categories: object with percentage and points data
    // For ungraded categories: the full object with numCompleted
    const categoryTotals = {};
    for (const category of v2Data.categories) {
      const catTotal = studentCategoryTotals[category.id];
      if (catTotal) {
        if (category.isUngraded || catTotal.isUngraded) {
          // For ungraded categories, store the full object with numCompleted
          categoryTotals[category.id] = {
            isUngraded: true,
            numCompleted: catTotal.numCompleted || 0,
          };
        } else {
          // For graded categories, store object with percentage and points data
          const grade = catTotal.studentGrade;
          categoryTotals[category.id] = {
            percentage: grade !== null && grade !== undefined ? grade * 100 : 0,
            studentGrade: grade,
            studentPoints: catTotal.studentPoints,
            assigned_points: catTotal.assigned_points,
            weightedCategoryTotal: catTotal.weightedCategoryTotal,
          };
        }
      } else {
        // Fallback if no pre-calculated data
        categoryTotals[category.id] = category.isUngraded
          ? { isUngraded: true, numCompleted: 0 }
          : {
              percentage: 0,
              studentGrade: 0,
              studentPoints: 0,
              assigned_points: 0,
            };
      }
    }

    // Convert grades Map to object
    const gradesByAssignment = {};
    studentGrades.forEach((grade, assignmentId) => {
      gradesByAssignment[assignmentId] = grade;
    });

    // Use pre-calculated course total from V1 data (stored on student object)
    // The grade is already a decimal (0-1), so convert to percentage
    const courseTotal =
      student.courseTotal !== null && student.courseTotal !== undefined
        ? student.courseTotal * 100
        : 0;

    studentCalculations[student.id] = {
      courseTotal,
      // Pass through course points data for points mode display
      coursePointsEarned: student.coursePointsEarned,
      coursePointsPossible: student.coursePointsPossible,
      categoryTotals,
      gradesByAssignment,
    };
  }

  return {
    studentCalculations,
    averages: v2Data.averages,
  };
}

export default transformReduxToV2Format;
